import { Component, OnInit, ViewEncapsulation, AfterViewInit, OnDestroy } from '@angular/core';
import { modal, ModalBase } from 'curvy';

function clamp(val: number, min: number, max: number) {
	if (val < min) return min;
	if (val > max) return max;
	return val;
}

@Component({
	selector: 'task-image-dialog',
	encapsulation: ViewEncapsulation.None,
	template: `
		<button class="round fake-button left"
			*ngIf="images.length>1"
			(click)="slide(-1)"
			color="background-dark"
		>
			<i icon="keyboard_arrow_left"></i>
		</button>

		<img class="main-image"
			(pointerdown)="drag_image($event)"
			[style.--zoom]="this.base_zoom * this.zoom"
			[style.--x]="this.pos.x + 'px'"
			[style.--y]="this.pos.y + 'px'"
			[src]="injectedData.imgs[selectedIndex]['src']"
		/>

		<button class="round fake-button right"
			*ngIf="images.length>1"
			(click)="slide(1)"
			color="background-dark"
		>
			<i icon="keyboard_arrow_right"></i>
		</button>

		<div *ngIf="injectedData.imgs.length>1" class="img-thumb-container">
			<div *ngFor="let img of injectedData.imgs; let i=index" class="img-thumb"
				[class.active]="i == selectedIndex"
			>
				<img [src]="img.src" (click)="selectImage(i)">
			</div>
		</div>

		<button class="zoom-out round" [class.no-thumbs]="injectedData.imgs.length<=1" color="secondary" [disabled]="target_zoom == 1" (click)="zoom_by(0.5)">
			<i icon="zoom_out"></i>
		</button>

		<button class="zoom-in round" [class.no-thumbs]="injectedData.imgs.length<=1" color="secondary" [disabled]="target_zoom == 16" (click)="zoom_by(2)">
			<i icon="zoom_in"></i>
		</button>

		<button class="close" color="primary-text" (click)="cancel()">
			<i icon="clear"></i>
			<span>{{ 'CLOSE' | trans }}</span>
		</button>
	`,
	styleUrls: ['./components.scss']
})
export class ImageDialog extends ModalBase<{imgs:object[], index: number}, void> implements OnInit, AfterViewInit, OnDestroy {
	preventSoftClose = false;
	parent = null;
	selectedIndex = 0;
	images = [];

	// @todo: calculate a good minimum zoom (image dimension div window dimension)
	alive = true;

	base_zoom = 1;
	zoom = 1;
	target_zoom = 1;

	ngOnInit(): void {
		this.parent = this.elRef.nativeElement.parentElement;
		this.parent.classList.add("imgs-modal");
		this.selectedIndex = this.injectedData.index;
		this.images = this.injectedData.imgs;

		const transition = () => {
			let img = this.parent.querySelector("img.main-image") as HTMLImageElement;
			let iw = img.naturalWidth;
			let ih = img.naturalHeight;

			this.base_zoom = 1;
			if (iw > window.innerWidth)
				this.base_zoom = Math.min(this.base_zoom, window.innerWidth/iw);
			if (ih > window.innerHeight)
				this.base_zoom = Math.min(this.base_zoom, window.innerHeight/ih);

			this.zoom = this.zoom + (this.target_zoom - this.zoom) * 0.2;
			if (Math.abs(this.zoom - this.target_zoom) < this.target_zoom*0.01) {
				this.zoom = this.target_zoom;
			}

			requestAnimationFrame(transition);
		};
		requestAnimationFrame(transition);
	}

	ngOnDestroy() {
		this.alive = false;
		this.parent.classList.remove("imgs-modal");
		this.drag_image("cleanup");
	}

	ngAfterViewInit() {
		const scrollContainer = document.querySelector(".img-thumb-container");
		if (!scrollContainer) return;

		scrollContainer.addEventListener("wheel", (evt) => {
			evt.preventDefault();
			scrollContainer.scrollLeft += evt["deltaY"];
		});

		this.constrain_image();
	}

	constrain_image() {
		let img = this.parent.querySelector("img.main-image") as HTMLImageElement;
		let iw = img.naturalWidth;
		let ih = img.naturalHeight;

		this.pos.x = clamp(this.pos.x, -iw/2, iw/2);
		this.pos.y = clamp(this.pos.y, -ih/2, ih/2);
	}

	selectImage(index) {
		this.selectedIndex = index;
		this.pos = {x: 0, y: 0, tx: 0, ty: 0, ptr: null};
		this.zoom = 1;
		this.target_zoom = 1;
	}

	drag: boolean = false;
	pos = {
		ptr: NaN,
		x: 0, y: 0,
		tx: 0, ty: 0,
	};
	drag_image = (e: PointerEvent|"cleanup") => {
		if (e == "cleanup") {
			window.removeEventListener("pointerup", this.drag_image);
			window.removeEventListener("pointermove", this.drag_image);
			document.body.style.userSelect = null;
			this.drag = null;
			return;
		}

		switch (e.type) {
			case "pointerdown": {
				if (!this.drag) {
					this.drag = true;
					window.addEventListener("pointerup", this.drag_image);
					window.addEventListener("pointermove", this.drag_image);
					document.body.style.userSelect = "none";
					this.pos.ptr = e.pointerId;
					this.pos.tx = e.clientX;
					this.pos.ty = e.clientY;
					return false;
				}
			};

			case "pointermove":
				if (this.drag && this.pos.ptr==e.pointerId) {
					let dx = e.clientX - this.pos.tx;
					let dy = e.clientY - this.pos.ty;
					this.pos.tx = e.clientX;
					this.pos.ty = e.clientY;
					this.pos.x += dx / (this.zoom * this.base_zoom);
					this.pos.y += dy / (this.zoom * this.base_zoom);
					this.constrain_image();
				}
			break;

			case "pointerup":
				if (this.drag && this.pos.ptr==e.pointerId) {
					this.drag_image("cleanup");
				}
			break;
		}
	}

	zoom_by(amt: number) {
		this.target_zoom *= amt;
		if (this.target_zoom < 1) this.target_zoom = 1;
		if (this.target_zoom > 16) this.target_zoom = 16;
	}

	slide(dir:number) {
		if (dir == 1) {
			this.selectedIndex = this.selectedIndex == this.images.length-1 ? 0 : ++this.selectedIndex;
		} else {
			this.selectedIndex = this.selectedIndex == 0 ? this.images.length-1 : --this.selectedIndex;
		}
		this.pos = {x: 0, y: 0, tx: 0, ty: 0, ptr: null};
		this.zoom = 1;
		this.target_zoom = 1;
	}

	cancel() {
		this.submit(null);
	}
}
